import useModal from 'lib/hooks/useModal';
import useCountUp from 'lib/hooks/useCountUp';
import { inRn, mString } from 'lib/helpers/utility';

import Icon from 'components/shared/Icon/Icon';
import Button from 'components/shared/Button/Button';
import Typography from 'components/shared/Typography/Typography';
import ReceiptModal from 'components/shared/Receipt/ReceiptModal';

// Shared payment-success screen for check-in and the patient portal.
const PaymentSuccessScreen = ({ amount, transactionId, appointmentId, title, subtitle }) => {
  const { isOpen, openModal, closeModal } = useModal();
  const animatedAmount = useCountUp(inRn(amount), { duration: 1000 });
  const canViewReceipt = Boolean(transactionId || appointmentId);

  return (
    <div className="flex h-full flex-col items-center justify-center gap-8 p-8 text-center sm:gap-6 xs:gap-5 xs:p-5">
      <div className="relative flex items-center justify-center">
        <span
          aria-hidden
          className="absolute h-52 w-52 animate-breathe rounded-full bg-success-100"
        />
        <span
          aria-hidden
          className="absolute h-44 w-44 animate-ring-burst rounded-full border-2 border-solid border-success-300 opacity-0"
        />
        <Icon
          icon="illustration-payment-successful"
          size={176}
          className="relative animate-check-bounce"
        />
      </div>

      <div className="flex max-w-[460px] animate-enter-up-1 flex-col items-center gap-2">
        <Typography
          variant="h3"
          weight="semibold"
          color="neutral-900"
          className="!text-3xl sm:!text-2xl">
          {title || 'Payment complete'}
        </Typography>
        <Typography
          variant="p"
          size="medium"
          color="neutral-600"
          className="!text-lg sm:!text-base">
          {subtitle || 'The payment was processed.'}
        </Typography>
      </div>

      <div className="flex w-full max-w-[440px] animate-enter-up-2 items-baseline justify-between gap-3 rounded-2xl border border-solid border-success-100 bg-gradient-to-b from-white to-success-50 p-6 text-left shadow-medium xs:p-5">
        <span className="text-base font-500 text-neutral-600">Total paid</span>
        <span className="text-3xl font-700 leading-none text-success-700 sm:text-2xl">
          {mString(animatedAmount)}
        </span>
      </div>

      {canViewReceipt && (
        <Button
          text="View receipt"
          icon="document-preview-mono"
          style="outlined"
          size="large"
          className="h-12 animate-enter-up-3 px-6 text-base"
          onClick={openModal}
        />
      )}

      <ReceiptModal
        isOpen={isOpen}
        onClose={closeModal}
        transactionId={transactionId}
        appointmentId={appointmentId}
      />
    </div>
  );
};

export default PaymentSuccessScreen;
